<template>
  <div class="dashboard-root pa-3 pa-sm-4 pa-md-6">
    <v-alert
      v-if="isUsingCachedData"
      type="warning"
      dense
      class="mb-4 rounded-lg"
      dismissible
    >
      Failed to load live metrics from server. Displaying cached data.
    </v-alert>

    <div class="d-flex align-center justify-space-between mb-5 flex-wrap">
      <div>
        <h1 class="page-title">Overview</h1>
        <p class="page-subtitle mb-0">
          Claim form &middot; Windscreen &middot; Minor damage (Next sprint)
          &middot; Major damage (Next sprint)
        </p>
      </div>
      <v-btn
        color="#0052cc"
        depressed
        dark
        class="refresh-btn text-capitalize"
        :loading="loading"
        @click="fetchStatusDistribution"
      >
        Refresh data
      </v-btn>
    </div>

    <v-row class="mb-5">
      <v-col cols="12" md="10" lg="8" xl="6" class="d-flex">
        <v-card outlined class="analytics-card w-100 pa-5">
          <div class="d-flex align-center justify-space-between mb-4">
            <h2 class="card-heading mb-0">Status distribution</h2>
            <div class="d-flex align-center card-tabs">
              <v-tooltip v-for="tab in filterTabs" :key="tab.key" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="tab-btn d-flex align-center justify-center"
                    :class="{ active: activeTab === tab.key }"
                    v-bind="attrs"
                    v-on="on"
                    @click="selectTab(tab.key)"
                    :aria-label="tab.label"
                  >
                    <v-icon
                      small
                      :color="activeTab === tab.key ? '#0052cc' : '#334155'"
                    >
                      {{ tab.icon }}
                    </v-icon>
                  </button>
                </template>
                <span>{{ tab.label }}</span>
              </v-tooltip>
            </div>
          </div>

          <div
            class="d-flex flex-column flex-sm-row align-center justify-space-between pt-2"
          >
            <div
              class="d-flex align-center justify-center mr-sm-4 mb-4 mb-sm-0 flex-shrink-0"
            >
              <svg
                width="150"
                height="150"
                viewBox="0 0 160 160"
                role="img"
                aria-label="Status distribution chart"
              >
                <title>Status distribution chart</title>
                <circle
                  cx="80"
                  cy="80"
                  r="56"
                  fill="transparent"
                  stroke="#f1f5f9"
                  stroke-width="17"
                />
                <circle
                  v-for="seg in donutSegments"
                  :key="seg.key"
                  cx="80"
                  cy="80"
                  r="56"
                  fill="transparent"
                  :stroke="seg.stroke"
                  stroke-width="17"
                  :stroke-dasharray="seg.dasharray"
                  :stroke-dashoffset="seg.offset"
                  transform="rotate(-90 80 80)"
                  class="donut-circle"
                />
                <text x="80" y="76" text-anchor="middle" class="donut-total">
                  {{ metrics.total }}
                </text>
                <text x="80" y="95" text-anchor="middle" class="donut-label">
                  Total claims
                </text>
              </svg>
            </div>

            <div class="w-100">
              <div
                v-for="item in legendItems"
                :key="item.key"
                class="d-flex align-center justify-space-between py-2 border-bottom"
              >
                <div class="d-flex align-center">
                  <div
                    class="legend-icon d-flex align-center justify-center rounded mr-3 flex-shrink-0"
                    :class="item.bg"
                  >
                    <v-icon small :color="item.color">{{ item.icon }}</v-icon>
                  </div>
                  <div>
                    <div class="legend-label-primary">
                      {{ item.label }}
                    </div>
                    <div class="legend-label-sub">
                      {{ item.sub }}
                    </div>
                  </div>
                </div>
                <div class="legend-number">{{ item.count }}</div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  ClaimsOverviewService,
  CACHED_STATUS_DISTRIBUTION,
} from '@/services/claims-overview-service'

const CIRCUMFERENCE = 2 * Math.PI * 56
const TAB_TYPES = Object.freeze({
  claim_form: 'CLAIM_FORM',
  windscreen: 'WINDSCREEN',
  minor: 'MINOR_ACCIDENT',
  major: 'MAJOR_ACCIDENT',
})
const STATUS_ITEMS = [
  'draft:Draft:Awaiting submission:#94a3b8:#334155:grey lighten-4:mdi-file-document-outline',
  'pendingSending:Pending / Sending:In review / sending:#0284c7:#0369a1:light-blue lighten-5:mdi-file-search-outline',
  'completeInGis:Complete / In GIS:Successfully posted:#10b981:#166534:green lighten-5:mdi-check-circle-outline',
  'actionRequired:Action Required:Action required:#f59e0b:#d97706:amber lighten-4:mdi-alert-circle-outline',
  'validationFailed:Validation Failed:Validation error:#ef4444:#dc2626:red lighten-5:mdi-close-circle-outline',
].map((s) => {
  const [key, label, sub, stroke, color, bg, icon] = s.split(':')
  return { key, label, sub, stroke, color, bg, icon }
})
const FILTER_TABS = [
  'all:All Claims:mdi-clipboard-text',
  'claim_form:Claim Form:mdi-file-document-edit',
  'windscreen:Windscreen:mdi-car-windshield',
  'minor:Minor Damage:mdi-car-wrench',
  'major:Major Damage:mdi-tow-truck',
].map((s) => {
  const [key, label, icon] = s.split(':')
  return { key, label, icon }
})

export default {
  name: 'ClaimsDashboardView',
  data: () => ({
    loading: false,
    isUsingCachedData: false,
    offlineReason: '',
    activeTab: 'all',
    filterTabs: FILTER_TABS,
    metrics: { ...CACHED_STATUS_DISTRIBUTION },
    timer: null,
  }),
  computed: {
    donutSegments() {
      const total = this.metrics.total || 0
      let offset = 0
      return STATUS_ITEMS.map((cfg) => {
        const len = total ? (this.metrics[cfg.key] / total) * CIRCUMFERENCE : 0
        const seg = {
          key: cfg.key,
          stroke: cfg.stroke,
          dasharray: `${len} ${CIRCUMFERENCE}`,
          offset,
        }
        offset -= len
        return seg
      })
    },
    legendItems() {
      return STATUS_ITEMS.map((it) => ({
        ...it,
        count: this.metrics[it.key] || 0,
      }))
    },
  },
  mounted() {
    this.fetchStatusDistribution()
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  },
  methods: {
    selectTab(tabKey) {
      this.activeTab = tabKey
      this.fetchStatusDistribution()
    },
    async fetchStatusDistribution() {
      this.loading = true
      try {
        const res = await ClaimsOverviewService.getStatusDistribution(
          TAB_TYPES[this.activeTab]
        )
        if (res && res.data) {
          this.metrics = {
            ...res.data,
            total: Number.isFinite(res.data.total) ? res.data.total : 0,
          }
          this.isUsingCachedData = false
          this.offlineReason = ''
        }
      } catch (err) {
        // Fall back to cached metrics when backend is unreachable or returns error
        this.metrics = { ...CACHED_STATUS_DISTRIBUTION }
        this.isUsingCachedData = true
        this.offlineReason = err?.message || 'Failed to load live metrics'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.dashboard-root {
  font-family: trebuc, sans-serif;
  background-color: #f3f6fa;
  min-height: 100vh;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0b2545;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
}
.page-subtitle {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-top: 3px;
}
.v-btn.refresh-btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 22px;
}
.v-sheet.v-card.analytics-card {
  border-radius: 16px;
  border-color: #eef2f6;
  background-color: #ffffff;
}
.card-heading {
  font-size: 15px;
  font-weight: 700;
  color: #0b2545;
}
.card-tabs {
  gap: 6px;
}
.tab-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.tab-btn.active {
  background-color: #eff6ff;
  border-color: #0052cc;
}
.donut-circle {
  transition: stroke-dasharray 0.35s ease, stroke-dashoffset 0.35s ease;
}
.donut-total {
  font-family: trebuc, sans-serif;
  font-weight: 800;
  font-size: 24px;
  fill: #0b2545;
}
.donut-label {
  font-family: trebuc, sans-serif;
  font-size: 11px;
  font-weight: 500;
  fill: #475569;
}
.legend-icon {
  width: 28px;
  height: 28px;
}
.legend-label-primary {
  font-size: 13px;
  font-weight: 700;
  color: #0b2545;
  line-height: 1.2;
}
.legend-label-sub {
  font-size: 11px;
  font-weight: 400;
  color: #475569;
  line-height: 1.2;
  margin-top: 2px;
}
.legend-number {
  font-size: 16px;
  font-weight: 800;
  color: #0b2545;
  text-align: right;
  min-width: 24px;
}
.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}
.border-bottom:last-child {
  border-bottom: none;
}
</style>
